import { useState, useRef, useEffect } from "react";

function TimeInput({
  initialTime = "01:00 AM",
  day,
  index,
  handleEditTime,
  isHighlighted,
  disabled = false,
}) {
  const parseTime = (value) => {
    if (!value) return { hhmm: "", ampm: "AM" };
    const parts = value.split(" ");
    return { hhmm: parts[0] || "", ampm: parts[1] || "AM" };
  };

  const [hhmm, setHhmm] = useState(() => parseTime(initialTime).hhmm);
  const [ampm, setAmpm] = useState(() => parseTime(initialTime).ampm);
  const inputRef = useRef(null);

  useEffect(() => {
    const parsed = parseTime(initialTime);
    if (parsed.hhmm !== hhmm || parsed.ampm !== ampm) {
      setHhmm(parsed.hhmm);
      setAmpm(parsed.ampm);
    }
  }, [initialTime]);

  const emitChange = (newHhmm, newAmpm) => {
    if (!newHhmm) {
      handleEditTime(day, index, "");
    } else {
      handleEditTime(day, index, `${newHhmm} ${newAmpm}`);
    }
  };

  const handleAmpmToggle = (value) => {
    if (disabled) return;
    setAmpm(value);
    emitChange(hhmm, value);
  };

  const handleBlur = () => {
    emitChange(hhmm, ampm);
  };

  const handleKeyDown = (e) => {
    const { selectionStart } = e.target;
    const isAllSelected =
      e.target.selectionStart === 0 && e.target.selectionEnd === hhmm.length;

    if (isAllSelected && e.key === "Backspace") {
      e.preventDefault();
      setHhmm("");
      return;
    }

    if (hhmm === "" && /[0-9]/.test(e.key)) {
      e.preventDefault();
      const newHhmm = `0${e.key}:00`;
      setHhmm(newHhmm);
      setCursorPos(1);
      return;
    }

    if (hhmm === "" && (e.key === "ArrowUp" || e.key === "ArrowDown")) {
      e.preventDefault();
      setHhmm("01:00");
      return;
    }

    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();
      const step = e.key === "ArrowUp" ? 1 : -1;
      if (selectionStart < 2) {
        setHhmm((prev) => adjustHhmm(prev, "hours", step));
      } else if (selectionStart >= 3) {
        setHhmm((prev) => adjustHhmm(prev, "minutes", step));
      }
      return;
    }

    if (e.key === "Backspace") {
      e.preventDefault();
      if (isAllSelected) { setHhmm(""); return; }
      if (selectionStart <= 2) {
        setHhmm((prev) => `00${prev.slice(2)}`);
      } else {
        setHhmm((prev) => `${prev.slice(0, 3)}00`);
      }
      return;
    }

    if (/[0-9]/.test(e.key)) {
      e.preventDefault();
      setHhmm((prev) => insertDigit(prev, e.key, selectionStart));
      const next = selectionStart === 1 ? 3 : selectionStart + 1;
      setCursorPos(next);
    }
  };

  const adjustHhmm = (current, part, step) => {
    const [h, m] = current.split(":").map(Number);
    let hours = isNaN(h) ? 1 : h;
    let minutes = isNaN(m) ? 0 : m;
    if (part === "hours") {
      hours += step;
      if (hours > 12) hours = 1;
      if (hours < 1) hours = 12;
    } else {
      minutes += step;
      if (minutes >= 60) { minutes -= 60; hours = hours >= 12 ? 1 : hours + 1; }
      if (minutes < 0) { minutes += 60; hours = hours <= 1 ? 12 : hours - 1; }
    }
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  };

  const insertDigit = (current, digit, position) => {
    let [h, m] = current.split(":");
    if (position < 2) {
      h = position === 0 ? digit + (h?.[1] || "0") : (h?.[0] || "0") + digit;
      const hNum = parseInt(h);
      if (hNum > 12) h = "12";
      if (hNum < 1) h = "01";
    } else if (position >= 3) {
      const mPos = position - 3;
      m = mPos === 0 ? digit + (m?.[1] || "0") : (m?.[0] || "0") + digit;
      if (parseInt(m) >= 60) m = "59";
    }
    return `${h}:${m}`;
  };

  const setCursorPos = (position) => {
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.selectionStart = position;
        inputRef.current.selectionEnd = position;
      }
    }, 0);
  };

  const border = isHighlighted ? "border-[#E00000]" : "border-quaternary";
  const textCol = isHighlighted ? "text-[#E00000]" : "text-quaternary";
  const divider = isHighlighted ? "bg-[#E00000]" : "bg-quaternary";

  return (
    <div className={`inline-flex items-stretch border-2 rounded-md overflow-hidden flex-shrink-0 ${border} ${disabled ? "opacity-70" : ""}`}>
      {/* HH:MM */}
      <input
        type="text"
        value={hhmm}
        onChange={(e) => setHhmm(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onDoubleClick={() => { if (!disabled) setHhmm(""); }}
        ref={inputRef}
        disabled={disabled}
        placeholder="HH:MM"
        className={`w-[4.2rem] text-center outline-none px-1 py-1 font-bold text-sm bg-white ${textCol} ${!disabled ? "hover:bg-[#fff0f0]" : ""}`}
      />

      {/* AM / PM pill */}
      <div className={`flex border-l-2 ${border}`}>
        <button
          type="button"
          disabled={disabled}
          onClick={() => handleAmpmToggle("AM")}
          className={`w-8 py-1 text-xs font-bold transition-colors ${
            ampm === "AM"
              ? "bg-quaternary text-white"
              : `bg-white ${textCol} ${!disabled ? "hover:bg-orange-50" : ""}`
          }`}
        >
          AM
        </button>
        <div className={`w-px self-stretch ${divider}`} />
        <button
          type="button"
          disabled={disabled}
          onClick={() => handleAmpmToggle("PM")}
          className={`w-8 py-1 text-xs font-bold transition-colors ${
            ampm === "PM"
              ? "bg-quaternary text-white"
              : `bg-white ${textCol} ${!disabled ? "hover:bg-orange-50" : ""}`
          }`}
        >
          PM
        </button>
      </div>
    </div>
  );
}

export default TimeInput;
